import React, { useCallback } from 'react';
import { graphql, withApollo } from 'react-apollo';
import { compose, mapProps } from 'recompose';
import PropTypes from 'prop-types';

import InputField from 'components/common/InputField/InputField';
import { toUTC, toLocalDate, toFormattedLocalDate } from 'utils/dateTime/converter';
import { NO_CACHE } from 'graphql/fetchPolicies';
import { getObjValue } from 'utils/functions';
import { useDebouncedStartTyping, useDebouncedStopTyping } from 'components/threepp/assessment/Answers/helper';
import {
  updateTemplateDateFieldValue,
  templateStartTyping as templateStartTypingMutation,
  templateStopTyping as templateStopTypingMutation,
  getLatestAssessmentEventById
} from 'graphql/operations';
import SaveTemplateAnswerHOC from '../SaveTemplateAnswerHOC';
import TemplateFieldUpdatesHOC from '../TemplateFieldUpdatesHOC';

import './DateAnswer.scss';

export function TemplateDateAnswer(props) {
  const { save, disabled, question, latestEvent, templateId, recordId, templateStartTyping, templateStopTyping } = props;

  // convert to local time
  const localTimeValue = question.dateValue ? toFormattedLocalDate(question.dateValue, 'YYYY-MM-DD') : '';
  const { calculated } = question;
  const action = getObjValue(latestEvent, 'action');
  const isDisabled = disabled || action === 'START_TYPING';

  const userStartTypingCallback = useDebouncedStartTyping(templateStartTyping, templateId, question.id, true, recordId);
  const userStopTypingCallback = useDebouncedStopTyping(templateStopTyping, templateId, question.id, true, recordId);

  const handleChangeCallback = useCallback(
    input => {
      userStartTypingCallback();
      userStopTypingCallback();
      const normalizedValue = input ? toUTC(toLocalDate(input)) : null;
      save(normalizedValue);
    },
    [save, userStartTypingCallback, userStopTypingCallback]
  );

  const handleValidationErrorCallback = useCallback(() => save.cancel(), [save]);

  return (
    <InputField
      classes="date-field-answer"
      type="date"
      name="answer"
      fullWidth
      disabled={calculated || isDisabled}
      value={localTimeValue}
      onChange={handleChangeCallback}
      onValidationError={handleValidationErrorCallback}
    />
  );
}

TemplateDateAnswer.defaultProps = {
  latestEvent: { action: '' }
};

TemplateDateAnswer.propTypes = {
  // TODO: Fix this...
  /* eslint-disable react/forbid-prop-types */
  question: PropTypes.object.isRequired,
  save: PropTypes.any.isRequired,
  disabled: PropTypes.bool.isRequired,
  templateStartTyping: PropTypes.func.isRequired,
  templateStopTyping: PropTypes.func.isRequired,
  latestEvent: PropTypes.object
  /* eslint-enable react/forbid-prop-types */
};

export const TemplateDateAnswerHOC = compose(
  withApollo,
  graphql(updateTemplateDateFieldValue, { name: 'fieldMutation' }),
  graphql(templateStartTypingMutation, { name: 'userStartTyping' }),
  graphql(templateStopTypingMutation, { name: 'userStopTyping' }),
  graphql(getLatestAssessmentEventById, {
    options: props => ({
      variables: {
        fieldId: props.question.id
      },
      fetchPolicy: NO_CACHE
    }),
    name: 'getLatestAssessmentEventById'
  }),
  SaveTemplateAnswerHOC,
  TemplateFieldUpdatesHOC,
  mapProps(props => ({
    question: props.question,
    disabled: props.disabled,
    templateId: props.assessmentId,
    recordId: props.recordId,
    save: props.save,
    templateStartTyping: props.userStartTyping,
    templateStopTyping: props.userStopTyping,
    latestEvent: props.getLatestAssessmentEventById.getLatestAssessmentEventById
  }))
);

export default TemplateDateAnswerHOC(TemplateDateAnswer);
